import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/react";
import { useCreateHeyLightOrder } from "~/api/salesForce/useCreateHeyLightOrder";
import { useFetchOpportunity } from "~/api/salesForce/useFetchOpportunity";
import Button from "~/components/atoms/Button/Button";
import Dropdown from "~/components/atoms/Dropdown/Dropdown";
import { DropdownOption } from "~/components/atoms/Dropdown/Dropdown.types";
import Input from "~/components/atoms/Input/Input";
import ConfirmModal from "~/components/molecules/ConfirmModal/ConfirmModal";
import { useCheckoutStore } from "~/stores/checkout/useCheckoutStore";
import { cn } from "~/utils";

const MAX_CONTRACT_AMOUNT = 10000;

export type FormData = {
  contractAmount: number | null;
  nationality: string;
  residenceStatus: string;
  dateOfEntry: string;
};

export type HeyLightContractFormProps = {
  className?: string;
  onSuccess: () => void;
  onSubmissionStateChange?: (isSubmitting: boolean) => void;
  isOpen?: boolean;
  discountedAmount?: number;
};

const InputField = ({
  label,
  ...props
}: { label: string } & Omit<React.ComponentProps<typeof Input>, "label">) => {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm text-grey-600">{label}</label>
      <Input {...props} />
    </div>
  );
};

const DropdownField = ({
  label,
  optional = false,
  value,
  ...props
}: {
  label: string;
  optional?: boolean;
  value?: DropdownOption;
} & Omit<React.ComponentProps<typeof Dropdown>, "label" | "value">) => {
  const intl = useIntl();
  return (
    <div className="flex flex-col gap-2 w-full">
      <label className="text-sm text-grey-600">
        {label}
        {optional && " (optional)"}
      </label>
      <Dropdown
        {...props}
        hasArrow={false}
        value={value}
        className="w-full"
        customTrigger={
          <div className="w-full">
            <button className="w-full flex items-center justify-between gap-3 rounded-full bg-beige-100 px-6 py-3 outline-none">
              <p className="text-sm text-left w-full truncate">
                {value?.title ||
                  intl.formatMessage({
                    id: "heylight.contract.dropdown.placeholder",
                  })}
              </p>
              <FontAwesomeIcon
                className="h-3 text-grey-400 flex-shrink-0"
                icon={faChevronDown}
              />
            </button>
          </div>
        }
      />
    </div>
  );
};

const HeyLightContractForm = ({
  className,
  onSuccess,
  onSubmissionStateChange,
  isOpen,
  discountedAmount,
}: HeyLightContractFormProps) => {
  const intl = useIntl();
  const { opportunityId } = useParams();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { data: opportunity, refetch: refetchOpportunity } =
    useFetchOpportunity({
      id: opportunityId as string,
    });

  const { mutateAsync: createHeyLightOrder, isLoading: creatingHeyLightOrder } =
    useCreateHeyLightOrder(opportunityId as string);

  const [
    contractAmount,
    setContractAmount,
    nationality,
    setNationality,
    residenceStatus,
    setResidenceStatus,
    dateOfEntry,
    setDateOfEntry,
    setHeyLightOrderId,
    heyLightOrderId,
  ] = useCheckoutStore((state) => [
    state.contractAmount,
    state.setContractAmount,
    state.nationality,
    state.setNationality,
    state.residenceStatus,
    state.setResidenceStatus,
    state.dateOfEntry,
    state.setDateOfEntry,
    state.setHeyLightOrderId,
    state.heyLightOrderId,
  ]);

  // Add a ref to track when we should call onSuccess
  const pendingSuccessCallback = useRef<string | null>(null);

  // Add useEffect to handle the success callback after state update
  useEffect(() => {
    if (
      pendingSuccessCallback.current &&
      heyLightOrderId === pendingSuccessCallback.current
    ) {
      // Clear the pending callback
      pendingSuccessCallback.current = null;
      // Call onSuccess now that we know the state is fully updated
      onSuccess();
    }
  }, [heyLightOrderId, onSuccess]);

  // Initialize the form when the modal opens
  useEffect(() => {
    if (isOpen) {
      refetchOpportunity().then(() => {
        setContractAmountFromOpportunity();
      });
    }
  }, [isOpen, refetchOpportunity]);

  const setContractAmountFromOpportunity = () => {
    if (discountedAmount !== undefined) {
      setContractAmount(Math.min(discountedAmount, MAX_CONTRACT_AMOUNT));
    } else if (opportunity) {
      const amount = opportunity.discountedAmount;
      if (amount) {
        setContractAmount(Math.min(amount, MAX_CONTRACT_AMOUNT));
      }
    }
  };

  // Notify parent about loading state changes
  useEffect(() => {
    onSubmissionStateChange?.(creatingHeyLightOrder);
  }, [creatingHeyLightOrder, onSubmissionStateChange]);

  const nationalityOptions: DropdownOption[] = [
    { id: "CHE", title: "Switzerland (CHE)" },
    { id: "GBR", title: "Great Britain (GBR)" },
    { id: "ITA", title: "Italy (ITA)" },
  ];

  const residenceStatusOptions: DropdownOption[] = [
    { id: "PERMIT_C", title: "Permit C" },
    { id: "PERMIT_B", title: "Permit B" },
    { id: "OTHER_RESIDENT_PERMIT", title: "Other Resident Permit" },
  ];

  const closeDropdown = () => {
    // Dispatch Escape key event to close the dropdown
    const event = new KeyboardEvent("keydown", { key: "Escape" });
    window.dispatchEvent(event);
  };

  const handleAmountChange = (value: string) => {
    const numValue = parseFloat(value);

    if (value === "") {
      setContractAmount(null);
    } else if (isNaN(numValue)) {
      // Invalid number input - ignore
      return;
    } else if (numValue < 0) {
      // Don't allow negative values
      return;
    } else {
      setContractAmount(numValue);
    }
  };

  const handleNationalityChange = (option: DropdownOption) => {
    setNationality(option.id);
    closeDropdown();
  };

  const handleResidenceStatusChange = (option: DropdownOption) => {
    setResidenceStatus(option.id);
    closeDropdown();
  };

  const handleDateOfEntryChange = (value: string | number) => {
    setDateOfEntry(value.toString());
  };

  const handleSubmit = async () => {
    if (!contractAmount || !opportunityId) {
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmedSubmit = async () => {
    if (!contractAmount || !opportunityId) {
      return;
    }

    // First dismiss the modal
    setShowConfirmModal(false);

    try {
      const response = await createHeyLightOrder({
        amount: contractAmount,
        nationality: nationality,
        residenceStatus: residenceStatus,
        dateOfEntry: dateOfEntry,
      });
      if (!response?.heyLightOrderId) {
        throw new Error("HeyLight order ID is missing in response");
      }

      // Store the expected heyLightOrderId before updating state
      pendingSuccessCallback.current = response.heyLightOrderId;

      // Update the state - useEffect will handle calling onSuccess
      setHeyLightOrderId(response.heyLightOrderId);
    } catch (error) {
      Sentry.captureException(error);
      // Reset the HeyLight order ID if there was an error
      setHeyLightOrderId(null);
      pendingSuccessCallback.current = null;
      // Don't call onSuccess() on error
    }
  };

  if (!opportunity) {
    return null;
  }

  return (
    <>
      <ConfirmModal
        open={showConfirmModal}
        setOpen={setShowConfirmModal}
        title={intl.formatMessage({ id: "heylight.creditCheck.confirm.title" })}
        description={intl.formatMessage({
          id: "heylight.creditCheck.confirm.description",
        })}
        buttonText={intl.formatMessage({
          id: "heylight.creditCheck.confirm.ok",
        })}
        buttonAction={handleConfirmedSubmit}
        onCancel={() => setShowConfirmModal(false)}
        className="min-w-[90%] text-center items-center justify-center"
      />
      <div className={cn("flex flex-col gap-4 justify-center", className)}>
        <div className="grid grid-cols-5 gap-x-16">
          {/* Left Column - Order Details */}
          <div className="flex flex-col gap-6 col-span-2">
            <div>
              <h2 className="text-xl mb-4">
                {intl.formatMessage({ id: "heylight.contract.title" })}
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <InputField
                  placeholder={intl.formatMessage({
                    id: "heylight.contract.orderReference",
                  })}
                  value={opportunity.opportunityId}
                  disabled
                  label={intl.formatMessage({
                    id: "heylight.contract.orderReference",
                  })}
                />
                <InputField
                  placeholder={intl.formatMessage({
                    id: "heylight.contract.amount",
                  })}
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={contractAmount !== null ? String(contractAmount) : ""}
                  onChange={handleAmountChange}
                  label={intl.formatMessage({ id: "heylight.contract.amount" })}
                  className="bg-beige-100 border-beige-100"
                />
              </div>
            </div>

            <div>
              <h2 className="text-xl mb-4">
                {intl.formatMessage({
                  id: "heylight.contract.billingAddress.title",
                })}
              </h2>
              <div className="flex flex-col gap-4">
                <InputField
                  placeholder={intl.formatMessage({
                    id: "heylight.contract.billingAddress.street",
                  })}
                  value={opportunity.billingAddress.street}
                  disabled
                  label={intl.formatMessage({
                    id: "heylight.contract.billingAddress.street",
                  })}
                  className="w-full"
                />
                <InputField
                  placeholder={intl.formatMessage({
                    id: "heylight.contract.billingAddress.city",
                  })}
                  value={opportunity.billingAddress.city}
                  disabled
                  label={intl.formatMessage({
                    id: "heylight.contract.billingAddress.city",
                  })}
                  className="w-full"
                />
                <div className="flex gap-4">
                  <div className="flex-1">
                    <InputField
                      placeholder={intl.formatMessage({
                        id: "heylight.contract.billingAddress.postalCode",
                      })}
                      value={opportunity.billingAddress.postalCode}
                      disabled
                      label={intl.formatMessage({
                        id: "heylight.contract.billingAddress.postalCode",
                      })}
                      className="w-full"
                    />
                  </div>
                  <div className="flex-1">
                    <InputField
                      placeholder={intl.formatMessage({
                        id: "heylight.contract.billingAddress.country",
                      })}
                      value={opportunity.billingAddress.country}
                      disabled
                      label={intl.formatMessage({
                        id: "heylight.contract.billingAddress.country",
                      })}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Customer Details */}
          <div className="flex flex-col gap-4 col-span-3">
            <h2 className="text-xl">
              {intl.formatMessage({
                id: "heylight.contract.customerDetails.title",
              })}
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <InputField
                placeholder={intl.formatMessage({
                  id: "heylight.contract.customerDetails.email",
                })}
                value={opportunity.accountEmail}
                disabled
                label={intl.formatMessage({
                  id: "heylight.contract.customerDetails.email",
                })}
                className="w-full"
              />
              <InputField
                placeholder={intl.formatMessage({
                  id: "heylight.contract.customerDetails.phone",
                })}
                value={opportunity.accountPhone}
                disabled
                label={intl.formatMessage({
                  id: "heylight.contract.customerDetails.phone",
                })}
                className="w-full"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <InputField
                placeholder={intl.formatMessage({
                  id: "heylight.contract.customerDetails.firstName",
                })}
                value={opportunity.accountFirstName}
                disabled
                label={intl.formatMessage({
                  id: "heylight.contract.customerDetails.firstName",
                })}
                className="w-full"
              />
              <InputField
                placeholder={intl.formatMessage({
                  id: "heylight.contract.customerDetails.lastName",
                })}
                value={opportunity.accountLastName}
                disabled
                label={intl.formatMessage({
                  id: "heylight.contract.customerDetails.lastName",
                })}
                className="w-full"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <InputField
                placeholder={intl.formatMessage({
                  id: "heylight.contract.customerDetails.birthDate",
                })}
                value={opportunity.accountBirthdate?.toLocaleDateString()}
                disabled
                label={intl.formatMessage({
                  id: "heylight.contract.customerDetails.birthDate",
                })}
              />
              <DropdownField
                label={intl.formatMessage({
                  id: "heylight.contract.customerDetails.nationality",
                })}
                optional
                options={nationalityOptions}
                value={nationalityOptions.find((opt) => opt.id === nationality)}
                onChange={handleNationalityChange}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <DropdownField
                label={intl.formatMessage({
                  id: "heylight.contract.customerDetails.residenceStatus",
                })}
                optional
                options={residenceStatusOptions}
                value={residenceStatusOptions.find(
                  (opt) => opt.id === residenceStatus
                )}
                onChange={handleResidenceStatusChange}
              />
              <InputField
                type="date"
                value={dateOfEntry ?? ""}
                onChange={handleDateOfEntryChange}
                label={intl.formatMessage({
                  id: "heylight.contract.customerDetails.dateOfEntry",
                })}
                className="bg-beige-100 border-beige-100"
              />
            </div>
          </div>
        </div>
        <p className="text-sm text-grey-600 text-center mt-4 mb-4">
          {intl.formatMessage({
            id: "heylight.contract.personalInfo.updateNote",
          })}
        </p>
        <div className="w-full flex justify-center">
          <Button
            text={intl.formatMessage({ id: "heylight.contract.submit" })}
            className="w-full py-4 bg-black text-white rounded-full hover:bg-black/90"
            onClick={handleSubmit}
            loading={creatingHeyLightOrder}
          />
        </div>
      </div>
    </>
  );
};

export default HeyLightContractForm;
